import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { apiFetch } from "../../services/api";
import {
  exportRoadmapToPDF,
  exportRoadmapToExcel,
} from "../../utils/reportExport";
import { createDevConsole } from "../../utils/devLogger";

const console = createDevConsole();

const RoadmapReportViewer = ({ reportId, report, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [zoomLevel, setZoomLevel] = useState("month"); // "year", "quarter", "month"
  const [clusterBy, setClusterBy] = useState(null);
  const [showSuccessors, setShowSuccessors] = useState(true);
  const [showPredecessors, setShowPredecessors] = useState(false);
  const [expandedEntities, setExpandedEntities] = useState(new Set());
  const [entityColumnWidth, setEntityColumnWidth] = useState(200);
  const [isResizing, setIsResizing] = useState(false);
  const [startYear, setStartYear] = useState(new Date().getFullYear());
  const [capabilityLevels, setCapabilityLevels] = useState([1, 2, 3, 4, 5]); // For filtering capability levels
  const [clusterFilters, setClusterFilters] = useState({}); // Filters for each cluster type: { "organisation": ["Org1", "Org2"], ... }
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [filterSearchTerm, setFilterSearchTerm] = useState("");
  const [savingFilters, setSavingFilters] = useState(false);
  const [filtersDirty, setFiltersDirty] = useState(false); // Track if filters have been modified
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exporting, setExporting] = useState(false);
  const timelineScrollRef = useRef(null);
  const exportMenuRef = useRef(null);
  const resizeStartRef = useRef(null);
  const initialWidthRef = useRef(null);
  const filterPanelRef = useRef(null);

  // Close filter panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        filterPanelRef.current &&
        !filterPanelRef.current.contains(event.target)
      ) {
        setShowFilterPanel(false);
      }
      if (
        exportMenuRef.current &&
        !exportMenuRef.current.contains(event.target)
      ) {
        setShowExportMenu(false);
      }
    };
    if (showFilterPanel || showExportMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showFilterPanel, showExportMenu]);

  // Get report configuration
  const reportConfig = report?.configuration || {};
  const entityType = report?.entity_type || "application";

  useEffect(() => {
    if (reportId || report) {
      executeReport();
    }
  }, [reportId, report]);

  // Reset expanded entities and filter search when cluster changes
  // Initialize root nodes as expanded
  useEffect(() => {
    setFilterSearchTerm("");
    setShowFilterPanel(false);

    // Initialize root capability nodes as expanded when switching to capability clustering
    if (
      clusterBy === "business-capability" &&
      data?.data?.capability_tree?.tree
    ) {
      const rootIds = data.data.capability_tree.tree.map((node) => node.id);
      setExpandedEntities(new Set(rootIds));
    } else {
      setExpandedEntities(new Set());
    }
  }, [clusterBy, data?.data?.capability_tree]);

  const executeReport = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiFetch(
        `/api/reports/${reportId || report.id}/execute`,
        {
          method: "POST",
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to execute report");
      }

      const result = await response.json();
      if (result.success && result.result) {
        setData(result.result);
        setShowSuccessors(result.result.show_successors !== false);
        setShowPredecessors(result.result.show_predecessors === true);

        // Set initial clustering if configured
        const clusterTypes =
          result.result.cluster_by_types || reportConfig.clusterByTypes || [];
        if (clusterTypes.length > 0) {
          setClusterBy(clusterTypes[0]);
        }

        // Set capability levels from configuration
        if (result.result.capability_levels) {
          setCapabilityLevels(result.result.capability_levels);
        }

        // Load saved cluster filters from configuration
        const savedFilters = reportConfig.clusterFilters || {};
        setClusterFilters(savedFilters);
        setFiltersDirty(false);
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (err) {
      console.error("Error executing report:", err);
      setError(err.message || "Failed to execute report");
    } finally {
      setLoading(false);
    }
  };

  // Check if current cluster type is hierarchical (has tree structure)
  const isHierarchicalCluster =
    clusterBy === "business-capability" || clusterBy === "business-context";

  // Build tree structure for hierarchical cluster types
  const filterTreeData = useMemo(() => {
    if (!data || !data.data || !clusterBy) return null;

    // For business-capability, use the existing tree if available
    if (
      clusterBy === "business-capability" &&
      data.data.capability_tree?.tree
    ) {
      const tree = data.data.capability_tree.tree.map((node) => ({
        ...node,
        children: node.children || [],
      }));
      const hasUnassigned = data.data.capability_tree.unassigned?.length > 0;
      return { tree, hasUnassigned };
    }

    // For business-context, build tree from entity cluster data
    if (clusterBy === "business-context") {
      const entities = data.data.entities || [];
      const contextsMap = new Map();

      // Extract all business contexts from entities
      entities.forEach((entity) => {
        const clusterData = entity.cluster_data?.[clusterBy];
        if (clusterData) {
          // Handle multiple contexts per entity if available
          const contexts = Array.isArray(clusterData)
            ? clusterData
            : [clusterData];
          contexts.forEach((ctx) => {
            if (ctx.name && !contextsMap.has(ctx.name)) {
              contextsMap.set(ctx.name, {
                id: ctx.id || ctx.name,
                name: ctx.name,
                level: ctx.level || 1,
                parent_id: ctx.parent_id || null,
                children: [],
              });
            }
          });
        }
      });

      // Build hierarchy from parent relationships
      const contextsList = Array.from(contextsMap.values());
      const rootContexts = [];

      contextsList.forEach((ctx) => {
        if (ctx.parent_id && contextsMap.has(ctx.parent_id)) {
          const parent = contextsMap.get(ctx.parent_id);
          parent.children.push(ctx);
        } else if (!ctx.parent_id) {
          rootContexts.push(ctx);
        } else {
          // Parent not found, treat as root
          rootContexts.push(ctx);
        }
      });

      // If no hierarchy detected, return flat list as tree
      if (rootContexts.length === 0 && contextsList.length > 0) {
        return { tree: contextsList, hasUnassigned: false };
      }

      const hasUnassigned = entities.some(
        (e) => !e.cluster_data?.[clusterBy]?.name
      );
      return { tree: rootContexts, hasUnassigned };
    }

    return null;
  }, [data, clusterBy]);

  // Get available filter options for the current cluster type (flat list)
  const availableFilterOptions = useMemo(() => {
    if (!data || !data.data || !clusterBy) return [];

    const entities = data.data.entities || [];
    const optionsSet = new Set();

    // For hierarchical types with tree, extract from tree
    if (filterTreeData?.tree) {
      const extractNames = (node) => {
        optionsSet.add(node.name);
        if (node.children) {
          node.children.forEach(extractNames);
        }
      };
      filterTreeData.tree.forEach(extractNames);
      if (filterTreeData.hasUnassigned) {
        optionsSet.add("Unassigned");
      }
    } else {
      // For standard clustering, extract from entity cluster_data
      entities.forEach((entity) => {
        const clusterData = entity.cluster_data?.[clusterBy];
        const clusterName = clusterData?.name || "Unassigned";
        optionsSet.add(clusterName);
      });
    }

    return Array.from(optionsSet).sort((a, b) => {
      // Keep "Unassigned" at the end
      if (a === "Unassigned") return 1;
      if (b === "Unassigned") return -1;
      return a.localeCompare(b);
    });
  }, [data, clusterBy, filterTreeData]);

  // Get current filter for cluster type (empty array means show all)
  const currentClusterFilter = clusterFilters[clusterBy] || [];
  const isFilterActive = currentClusterFilter.length > 0;

  // Helper: Get all descendant names from a tree node
  const getAllDescendants = useCallback((node) => {
    const descendants = [node.name];
    if (node.children && node.children.length > 0) {
      node.children.forEach((child) => {
        descendants.push(...getAllDescendants(child));
      });
    }
    return descendants;
  }, []);

  // Helper: Check if all descendants are selected
  const areAllDescendantsSelected = useCallback(
    (node, selected) => {
      const descendants = getAllDescendants(node);
      return descendants.every((name) => selected.includes(name));
    },
    [getAllDescendants]
  );

  // Helper: Check if some (but not all) descendants are selected
  const areSomeDescendantsSelected = useCallback(
    (node, selected) => {
      const descendants = getAllDescendants(node);
      const selectedCount = descendants.filter((name) =>
        selected.includes(name)
      ).length;
      return selectedCount > 0 && selectedCount < descendants.length;
    },
    [getAllDescendants]
  );

  // Toggle a tree node (includes all descendants)
  const toggleTreeNode = useCallback(
    (node) => {
      setClusterFilters((prev) => {
        const current = prev[clusterBy] || [];
        const descendants = getAllDescendants(node);
        const allSelected = descendants.every((name) => current.includes(name));

        let updated;
        if (allSelected) {
          // Deselect all descendants
          updated = current.filter((name) => !descendants.includes(name));
        } else {
          // Select all descendants
          const newSelections = descendants.filter(
            (name) => !current.includes(name)
          );
          updated = [...current, ...newSelections];
        }

        setFiltersDirty(true);
        return { ...prev, [clusterBy]: updated };
      });
    },
    [clusterBy, getAllDescendants]
  );

  // Toggle a filter option (flat list)
  const toggleFilterOption = (option) => {
    setClusterFilters((prev) => {
      const current = prev[clusterBy] || [];
      const updated = current.includes(option)
        ? current.filter((o) => o !== option)
        : [...current, option];
      setFiltersDirty(true);
      return { ...prev, [clusterBy]: updated };
    });
  };

  // Select all filter options
  const selectAllFilters = () => {
    setClusterFilters((prev) => {
      setFiltersDirty(true);
      return { ...prev, [clusterBy]: [...availableFilterOptions] };
    });
  };

  // Clear all filter options (show all)
  const clearAllFilters = () => {
    setClusterFilters((prev) => {
      setFiltersDirty(true);
      return { ...prev, [clusterBy]: [] };
    });
  };

  // Tree filter node component
  const TreeFilterNode = ({
    node,
    depth = 0,
    selected,
    onToggle,
    searchTerm,
  }) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const hasChildren = node.children && node.children.length > 0;
    const isSelected = selected.includes(node.name);
    const isIndeterminate =
      !isSelected && areSomeDescendantsSelected(node, selected);
    const matchesSearch =
      !searchTerm || node.name.toLowerCase().includes(searchTerm.toLowerCase());
    const hasMatchingDescendant =
      hasChildren &&
      node.children.some((child) => {
        const checkMatch = (n) => {
          if (n.name.toLowerCase().includes(searchTerm.toLowerCase()))
            return true;
          return n.children?.some(checkMatch) || false;
        };
        return checkMatch(child);
      });

    // Hide if doesn't match search and no matching descendants
    if (searchTerm && !matchesSearch && !hasMatchingDescendant) {
      return null;
    }

    return (
      <div>
        <div
          className={`flex items-center gap-1 py-1 px-1 rounded cursor-pointer transition-colors hover:bg-gray-700 ${
            isSelected ? "bg-blue-600/20" : ""
          }`}
          style={{ paddingLeft: `${depth * 16 + 4}px` }}
        >
          {hasChildren ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
              className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-white flex-shrink-0"
            >
              {isExpanded ? "▼" : "▶"}
            </button>
          ) : (
            <span className="w-4 flex-shrink-0" />
          )}
          <label className="flex items-center gap-2 flex-1 cursor-pointer min-w-0">
            <input
              type="checkbox"
              checked={isSelected}
              ref={(el) => {
                if (el) el.indeterminate = isIndeterminate;
              }}
              onChange={() => onToggle(node)}
              className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-600 rounded focus:ring-blue-500 flex-shrink-0"
            />
            <span
              className={`text-sm truncate ${matchesSearch ? "text-gray-200" : "text-gray-400"}`}
            >
              {node.name}
            </span>
            {node.entity_count !== undefined && (
              <span className="text-xs text-gray-500 flex-shrink-0">
                ({node.entity_count})
              </span>
            )}
          </label>
        </div>
        {hasChildren && isExpanded && (
          <div>
            {node.children.map((child, idx) => (
              <TreeFilterNode
                key={child.id || child.name || idx}
                node={child}
                depth={depth + 1}
                selected={selected}
                onToggle={onToggle}
                searchTerm={searchTerm}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  // Save filters to report configuration
  const saveFilters = async () => {
    if (!report?.id) return;

    setSavingFilters(true);
    try {
      const updatedConfig = {
        ...reportConfig,
        clusterFilters: clusterFilters,
      };

      const response = await apiFetch(`/api/reports/${report.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          configuration: updatedConfig,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to save filters");
      }

      setFiltersDirty(false);
    } catch (err) {
      console.error("Error saving filters:", err);
      setError("Failed to save filters: " + err.message);
    } finally {
      setSavingFilters(false);
    }
  };

  const getLifecycleColor = (status) => {
    const colors = {
      Planned: "bg-blue-500",
      "Phase In": "bg-yellow-500",
      Active: "bg-green-500",
      "Phase Out": "bg-orange-500",
      "End of Life": "bg-red-500",
      Unknown: "bg-gray-500",
    };
    return colors[status] || colors["Unknown"];
  };

  const getLifecycleColorHex = (status) => {
    const colors = {
      Planned: "#3b82f6",
      "Phase In": "#eab308",
      Active: "#22c55e",
      "Phase Out": "#f97316",
      "End of Life": "#ef4444",
      Unknown: "#6b7280",
    };
    return colors[status] || colors["Unknown"];
  };

  const parseDate = (dateValue) => {
    if (!dateValue) return null;
    try {
      if (typeof dateValue === "string") {
        const dateStr = dateValue.split("T")[0];
        const [year, month, day] = dateStr.split("-").map(Number);
        return new Date(year, month - 1, day);
      }
      return new Date(dateValue);
    } catch {
      return null;
    }
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return null;
    try {
      const date =
        typeof dateValue === "string" ? new Date(dateValue) : dateValue;
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return String(dateValue);
    }
  };

  // Calculate timeline data with zoom support
  const timelineData = useMemo(() => {
    if (!data || !data.data || !data.data.entities) return null;

    const allDates = [];
    const entitySegments = [];
    const entityMap = new Map();

    // First pass: collect all dates and create segments
    data.data.entities.forEach((entity) => {
      entityMap.set(entity.id, entity);
      const timelineAttrs = entity.timeline_attributes || {};
      const segments = [];

      const dateKeys = [
        { key: "plan_start_date", status: "Planned" },
        { key: "phase_in_start_date", status: "Phase In" },
        { key: "active_start_date", status: "Active" },
        { key: "phase_out_start_date", status: "Phase Out" },
        { key: "eol_start_date", status: "End of Life" },
      ];

      dateKeys.forEach(({ key, status }) => {
        if (timelineAttrs[key]) {
          const date = parseDate(timelineAttrs[key]);
          if (date) {
            allDates.push(date);
            segments.push({ date, status, key });
          }
        }
      });

      // Sort segments by date
      segments.sort((a, b) => a.date - b.date);

      // Create timeline segments
      const timelineSegments = [];
      for (let i = 0; i < segments.length; i++) {
        const start = segments[i].date;
        const end = i < segments.length - 1 ? segments[i + 1].date : null;
        timelineSegments.push({
          start,
          end,
          status: segments[i].status,
          startKey: segments[i].key,
        });
      }

      entitySegments.push({
        entity,
        segments: timelineSegments,
        isSuccessor: false,
        predecessorId: null,
        level: 0,
      });
    });

    // If no dates, create a minimal timeline structure to show entities anyway
    if (allDates.length === 0) {
      const currentYear = new Date().getFullYear();
      return {
        intervals: [new Date(currentYear, 0, 1)],
        startDate: new Date(currentYear, 0, 1),
        endDate: new Date(currentYear, 11, 31),
        minYear: currentYear,
        maxYear: currentYear,
        entitySegments: entitySegments,
      };
    }

    // Find min and max dates
    const minDate = new Date(Math.min(...allDates));
    const maxDate = new Date(Math.max(...allDates));

    // Generate intervals based on zoom level
    let startDate, endDate, intervals;

    if (zoomLevel === "year") {
      startDate = new Date(minDate.getFullYear(), 0, 1);
      endDate = new Date(maxDate.getFullYear(), 11, 31);
      intervals = [];
      for (
        let year = startDate.getFullYear();
        year <= endDate.getFullYear();
        year++
      ) {
        intervals.push(new Date(year, 0, 1));
      }
    } else if (zoomLevel === "quarter") {
      startDate = new Date(
        minDate.getFullYear(),
        Math.floor(minDate.getMonth() / 3) * 3,
        1
      );
      endDate = new Date(
        maxDate.getFullYear(),
        Math.floor(maxDate.getMonth() / 3) * 3 + 3,
        0
      );
      intervals = [];
      const current = new Date(startDate);
      while (current <= endDate) {
        intervals.push(new Date(current));
        current.setMonth(current.getMonth() + 3);
      }
    } else {
      // month
      startDate = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
      endDate = new Date(maxDate.getFullYear(), maxDate.getMonth() + 1, 0);
      intervals = [];
      const current = new Date(startDate);
      while (current <= endDate) {
        intervals.push(new Date(current));
        current.setMonth(current.getMonth() + 1);
      }
    }

    return {
      startDate,
      endDate,
      intervals,
      entitySegments,
      zoomLevel,
      minYear: minDate.getFullYear(),
      maxYear: maxDate.getFullYear(),
    };
  }, [data, zoomLevel]);

  // Filter timeline intervals by start year
  const filteredTimelineData = useMemo(() => {
    if (!timelineData) return null;

    const filteredIntervals = timelineData.intervals.filter((interval) => {
      return interval.getFullYear() >= startYear;
    });

    // Adjust startDate if needed
    let filteredStartDate = timelineData.startDate;
    if (filteredIntervals.length > 0) {
      filteredStartDate = filteredIntervals[0];
    }

    return {
      ...timelineData,
      intervals: filteredIntervals,
      startDate: filteredStartDate,
    };
  }, [timelineData, startYear]);

  // Set default start year to current year when timeline data is available
  useEffect(() => {
    if (timelineData && timelineData.minYear) {
      const currentYear = new Date().getFullYear();
      // Only set if current year is within the data range
      if (
        currentYear >= timelineData.minYear &&
        currentYear <= timelineData.maxYear
      ) {
        setStartYear(currentYear);
      } else if (currentYear < timelineData.minYear) {
        setStartYear(timelineData.minYear);
      } else {
        setStartYear(timelineData.maxYear);
      }
    }
  }, [timelineData]);

  // Helper to flatten capability tree into rows for display
  // Respects capability level filter and expansion state
  const flattenCapabilityTree = (tree, expandedNodes = new Set()) => {
    const rows = [];

    if (!tree || !tree.tree) return rows;

    // Helper to collect all entities from a node and its descendants (for counting)
    const collectAllEntities = (node) => {
      let allEntities = [...(node.entities || [])];
      if (node.children && node.children.length > 0) {
        node.children.forEach((child) => {
          allEntities = [...allEntities, ...collectAllEntities(child)];
        });
      }
      return allEntities;
    };

    // Helper to count child capabilities
    const countChildCapabilities = (node) => {
      let count = 0;
      if (node.children && node.children.length > 0) {
        node.children.forEach((child) => {
          const childLevel = child.level || (node.level || 1) + 1;
          if (capabilityLevels.includes(childLevel)) {
            count++;
          }
          count += countChildCapabilities(child);
        });
      }
      return count;
    };

    const processNode = (
      node,
      depth = 0,
      parentPath = "",
      parentExpanded = true
    ) => {
      // If parent is not expanded, don't process this node
      if (!parentExpanded) return [];

      const currentPath = parentPath
        ? `${parentPath} > ${node.name}`
        : node.name;
      const nodeLevel = node.level || 1;

      // Check if this node's level is in the selected levels
      const shouldShowCapability = capabilityLevels.includes(nodeLevel);

      // Collect entities directly under this node
      let directEntities = [...(node.entities || [])];

      // Check if this node is expanded (root nodes initialized as expanded via useEffect)
      const isExpanded = expandedNodes.has(node.id);

      // If this capability level is selected, show it
      if (shouldShowCapability) {
        // Count all entities (including from children) for display
        const totalEntityCount = collectAllEntities(node).length;
        const hasChildren =
          countChildCapabilities(node) > 0 || directEntities.length > 0;

        // Add capability row
        rows.push({
          type: "capability",
          id: node.id,
          name: node.name,
          level: nodeLevel,
          depth: depth,
          path: currentPath,
          entityCount: totalEntityCount,
          isExpanded: isExpanded,
          hasChildren: hasChildren,
          children: node.children || [],
        });

        // Only process children and entities if expanded
        if (isExpanded) {
          // Process child capabilities
          if (node.children && node.children.length > 0) {
            node.children.forEach((child) => {
              processNode(child, depth + 1, currentPath, true);
            });
          }

          // Add entities under this capability
          if (directEntities.length > 0) {
            directEntities.forEach((entity) => {
              rows.push({
                type: "entity",
                id: entity.id,
                name: entity.name,
                entity: entity,
                depth: depth + 1,
                parentCapability: node.id,
                parentPath: currentPath,
              });
            });
          }
        }

        return [];
      } else {
        // This level is not selected, process children anyway (they might be at a selected level)
        if (node.children && node.children.length > 0) {
          node.children.forEach((child) => {
            processNode(child, depth, parentPath, parentExpanded);
          });
        }
        // Return entities for aggregation to parent
        return directEntities;
      }
    };

    // Process all root nodes
    if (Array.isArray(tree.tree)) {
      tree.tree.forEach((root) => {
        processNode(root, 0, "", true);
      });
    }

    // Add unassigned entities
    if (tree.unassigned && tree.unassigned.length > 0) {
      const isUnassignedExpanded = expandedNodes.has("unassigned") || true; // Unassigned expanded by default

      rows.push({
        type: "capability",
        id: "unassigned",
        name: "Unassigned",
        level: 0,
        depth: 0,
        path: "Unassigned",
        entityCount: tree.unassigned.length,
        isExpanded: isUnassignedExpanded,
        hasChildren: tree.unassigned.length > 0,
        children: [],
      });

      if (isUnassignedExpanded) {
        tree.unassigned.forEach((entity) => {
          rows.push({
            type: "entity",
            id: entity.id,
            name: entity.name,
            entity: entity,
            depth: 1,
            parentCapability: "unassigned",
            parentPath: "Unassigned",
          });
        });
      }
    }

    return rows;
  };

  // Get entities to display (grouped or flat)
  const displayData = useMemo(() => {
    if (!data || !data.data) return {};

    const entities = data.data.entities || [];
    const activeFilter = clusterFilters[clusterBy] || [];
    const hasActiveFilter = activeFilter.length > 0;

    // If clustering is enabled and a cluster type is selected, re-group dynamically
    if (clusterBy && data.clustering_enabled && entities.length > 0) {
      // For business-capability with hierarchy, use the tree structure from backend
      if (clusterBy === "business-capability" && data.data.capability_tree) {
        // Apply filter to tree if active
        if (hasActiveFilter) {
          const filterTree = (tree) => {
            const filteredTree =
              tree.tree
                ?.filter((node) => {
                  // Check if this node or any descendant matches the filter
                  const matchesFilter = (n) => {
                    if (activeFilter.includes(n.name)) return true;
                    return n.children?.some(matchesFilter) || false;
                  };
                  return matchesFilter(node);
                })
                .map((node) => {
                  // Recursively filter children
                  const filterNode = (n) => {
                    if (!n.children || n.children.length === 0) return n;
                    const filteredChildren = n.children
                      .filter((child) => {
                        const matchesFilter = (c) => {
                          if (activeFilter.includes(c.name)) return true;
                          return c.children?.some(matchesFilter) || false;
                        };
                        return matchesFilter(child);
                      })
                      .map(filterNode);
                    return { ...n, children: filteredChildren };
                  };
                  return filterNode(node);
                }) || [];

            const filteredUnassigned = activeFilter.includes("Unassigned")
              ? tree.unassigned
              : [];

            return {
              tree: filteredTree,
              unassigned: filteredUnassigned,
            };
          };

          return {
            _isTree: true,
            _tree: filterTree(data.data.capability_tree),
          };
        }

        // Return tree structure for hierarchical rendering
        return {
          _isTree: true,
          _tree: data.data.capability_tree,
        };
      }

      // For business-capability without tree (fallback), use grouped data
      if (clusterBy === "business-capability" && data.data.grouped) {
        const filtered = {};
        Object.entries(data.data.grouped).forEach(
          ([groupName, groupEntities]) => {
            // Apply filter if active
            if (!hasActiveFilter || activeFilter.includes(groupName)) {
              filtered[groupName] = groupEntities;
            }
          }
        );
        return filtered;
      }

      // Standard flat clustering for other types
      const grouped = {};

      entities.forEach((entity) => {
        // Get cluster data specifically for the selected cluster type
        // Only use cluster_data[clusterBy], not the fallback to entity.cluster
        // because entity.cluster might be for a different cluster type
        const clusterData = entity.cluster_data?.[clusterBy];
        const clusterName = clusterData?.name || "Unassigned";

        // Apply filter if active
        if (hasActiveFilter && !activeFilter.includes(clusterName)) {
          return; // Skip this entity
        }

        if (!grouped[clusterName]) {
          grouped[clusterName] = [];
        }
        grouped[clusterName].push(entity);
      });

      return grouped;
    }

    // If no clustering or no cluster selected, return flat list
    return { "All Entities": entities };
  }, [data, clusterBy, capabilityLevels, clusterFilters]);

  // Flatten tree for rendering if it's a tree structure
  // Re-compute when capability levels change
  const treeRows = useMemo(() => {
    if (displayData._isTree && displayData._tree) {
      return flattenCapabilityTree(displayData._tree, expandedEntities);
    }
    return null;
  }, [displayData, expandedEntities, capabilityLevels]);

  // Build export data from the current UI state (filtered, grouped, etc.)
  const buildExportData = useCallback(() => {
    const exportRows = [];

    // Helper to build entity export row
    const buildEntityExportRow = (entity) => ({
      id: entity.id,
      name: entity.name,
      description: entity.description,
      lifecycle_status: entity.lifecycle_status,
      timeline_attributes: entity.timeline_attributes || {},
      successors: entity.successors || [],
    });

    // Check what display mode we're in
    if (displayData && displayData._isTree && treeRows) {
      // Capability tree view - use treeRows which respects filters and expansion
      treeRows.forEach((row) => {
        if (row.type === "capability") {
          exportRows.push({
            _isGroup: true,
            groupName: row.name,
            level: row.depth,
            entityCount: row.entityCount,
          });
        } else if (row.type === "entity" && row.entity) {
          exportRows.push({
            _isGroup: false,
            groupName: row.parentPath || "",
            ...buildEntityExportRow(row.entity),
          });
        }
      });
    } else if (displayData && !displayData._isTree) {
      // Flat grouped view
      Object.entries(displayData).forEach(([groupName, entities]) => {
        if (!Array.isArray(entities)) return; // Skip non-array entries

        // Dedupe entities
        const uniqueEntities = entities.filter(
          (entity, idx, self) =>
            idx === self.findIndex((e) => e.id === entity.id)
        );

        if (clusterBy && Object.keys(displayData).length > 1) {
          // Add group header
          exportRows.push({
            _isGroup: true,
            groupName: groupName,
            entityCount: uniqueEntities.length,
          });
        }

        // Add entities
        uniqueEntities.forEach((entity) => {
          exportRows.push({
            _isGroup: false,
            groupName: groupName !== "All Entities" ? groupName : "",
            ...buildEntityExportRow(entity),
          });
        });
      });
    }

    // Count total entities
    const totalEntities = exportRows.filter((r) => !r._isGroup).length;

    return {
      entity_type: data?.entity_type || report?.entity_type || "application",
      generated_at: data?.generated_at,
      clustering_enabled: data?.clustering_enabled,
      cluster_by: clusterBy,
      cluster_by_types: data?.cluster_by_types || [],
      data: {
        total_entities: totalEntities,
        rows: exportRows,
      },
    };
  }, [displayData, treeRows, clusterBy, data, report]);

  // Handle export - uses filtered/displayed data from UI state
  const handleExport = useCallback(
    async (format) => {
      setExporting(true);
      try {
        // Build export data that reflects exactly what's shown in the UI
        const exportData = buildExportData();

        if (format === "pdf") {
          exportRoadmapToPDF(report, exportData);
        } else if (format === "xlsx") {
          exportRoadmapToExcel(report, exportData);
        }
      } catch (err) {
        console.error("Export error:", err);
        setError("Failed to export report: " + err.message);
      } finally {
        setExporting(false);
        setShowExportMenu(false);
      }
    },
    [buildExportData, report]
  );

  const toggleEntityExpansion = (entityId) => {
    setExpandedEntities((prev) => {
      const next = new Set(prev);
      if (next.has(entityId)) {
        next.delete(entityId);
      } else {
        next.add(entityId);
      }
      return next;
    });
  };

  // Handle column resizing
  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    resizeStartRef.current = e.clientX;
    initialWidthRef.current = entityColumnWidth;
    setIsResizing(true);
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (
        !isResizing ||
        resizeStartRef.current === null ||
        initialWidthRef.current === null
      )
        return;

      const deltaX = e.clientX - resizeStartRef.current;
      const newWidth = initialWidthRef.current + deltaX;

      // Set minimum and maximum width constraints
      if (newWidth >= 150 && newWidth <= 600) {
        setEntityColumnWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      resizeStartRef.current = null;
      initialWidthRef.current = null;
      // Reset cursor immediately when mouse is released
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      // Always reset cursor on cleanup
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing]);

  // Calculate position and width for a segment in the timeline
  const getSegmentStyle = (segment, startDate, endDate) => {
    const timelineStart = startDate.getTime();
    const timelineEnd = endDate.getTime();
    const timelineDuration = timelineEnd - timelineStart;

    if (timelineDuration <= 0) {
      return { left: "0%", width: "0%" };
    }

    const segmentStart = segment.start.getTime();
    const segmentEnd = segment.end ? segment.end.getTime() : timelineEnd;

    const left = ((segmentStart - timelineStart) / timelineDuration) * 100;
    const width = ((segmentEnd - segmentStart) / timelineDuration) * 100;

    return {
      left: `${Math.max(0, left)}%`,
      width: `${Math.min(100 - left, width)}%`,
    };
  };

  // Format interval label based on zoom level
  const formatIntervalLabel = (date, zoomLevel) => {
    if (zoomLevel === "year") {
      return date.getFullYear().toString();
    } else if (zoomLevel === "quarter") {
      const quarter = Math.floor(date.getMonth() / 3) + 1;
      return `Q${quarter} ${date.getFullYear()}`;
    } else {
      return date.toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
      });
    }
  };

  // Get interval width based on zoom level
  const getIntervalWidth = (zoomLevel) => {
    if (zoomLevel === "year") return 120;
    if (zoomLevel === "quarter") return 100;
    return 80; // month
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="bg-gray-800 rounded-lg p-6 shadow-xl">
          <svg
            className="animate-spin w-8 h-8 text-blue-400 mb-3 mx-auto"
            viewBox="0 0 24 24"
            fill="none"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <p className="text-sm text-gray-200 text-center">Loading report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-900/50 border border-red-700 rounded-lg p-4 text-red-200">
            <h2 className="text-lg font-semibold mb-2">Error Loading Report</h2>
            <p>{error}</p>
            {onClose && (
              <button
                onClick={onClose}
                className="mt-4 px-4 py-2 bg-red-700 hover:bg-red-600 text-white rounded-lg"
              >
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Handle case where there's no data or no timeline data
  if (!data || !data.data) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <div className="text-gray-400 mb-4">No report data available</div>
          <p className="text-gray-500 text-sm mb-6">
            This report doesn't have any data yet. Please check your entity filters or add entities to your system.
          </p>
          {onClose && (
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              Go Back to Reports
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!timelineData) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <div className="text-gray-400 mb-4">No timeline data available</div>
          <p className="text-gray-500 text-sm mb-6">
            {data.data?.total_entities > 0
              ? `This report has ${data.data.total_entities} entities, but none have timeline dates configured.`
              : "This report doesn't have any entities yet."}
          </p>
          {onClose && (
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              Go Back to Reports
            </button>
          )}
        </div>
      </div>
    );
  }

  // Generate year options for the filter
  const yearOptions = [];
  if (timelineData.minYear && timelineData.maxYear) {
    for (
      let year = timelineData.minYear;
      year <= timelineData.maxYear;
      year++
    ) {
      yearOptions.push(year);
    }
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              {report?.name || "Roadmap Report"}
              {filtersDirty && (
                <span className="text-xs bg-yellow-600 text-yellow-100 px-2 py-0.5 rounded-full font-normal">
                  Unsaved filters
                </span>
              )}
            </h1>
            <p className="text-sm text-gray-400 mt-1">
              {entityType} • {data.data?.total_entities || 0} entities
              {isFilterActive && (
                <span className="text-blue-400 ml-2">
                  • Filtered by {currentClusterFilter.length}{" "}
                  {clusterBy.replace("-", " ")}(s)
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {filtersDirty && (
              <button
                onClick={saveFilters}
                disabled={savingFilters}
                className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium transition-colors"
              >
                {savingFilters ? (
                  <svg
                    className="animate-spin w-4 h-4"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                    />
                  </svg>
                )}
                Save Filters
              </button>
            )}
            {/* Export Dropdown */}
            <div className="relative" ref={exportMenuRef}>
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                disabled={exporting}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg transition-colors"
              >
                {exporting ? (
                  <svg
                    className="animate-spin w-4 h-4"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                )}
                Export
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {showExportMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-gray-800 border border-gray-600 rounded-lg shadow-xl z-50">
                  <button
                    onClick={() => handleExport("pdf")}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left text-white hover:bg-gray-700 transition-colors rounded-t-lg"
                  >
                    <svg
                      className="w-5 h-5 text-red-400"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 2l5 5h-5V4zM8.5 15h1v2h-1v-2zm2 0h1v2h-1v-2zm2 0h1v2h-1v-2z" />
                    </svg>
                    <div>
                      <div className="font-medium">Export to PDF</div>
                      <div className="text-xs text-gray-400">
                        Print-ready format
                      </div>
                    </div>
                  </button>
                  <button
                    onClick={() => handleExport("xlsx")}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left text-white hover:bg-gray-700 transition-colors rounded-b-lg border-t border-gray-700"
                  >
                    <svg
                      className="w-5 h-5 text-green-400"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 2l5 5h-5V4zM8 17h8v1H8v-1zm0-2h8v1H8v-1zm0-2h4v1H8v-1z" />
                    </svg>
                    <div>
                      <div className="font-medium">Export to Excel</div>
                      <div className="text-xs text-gray-400">
                        Spreadsheet format
                      </div>
                    </div>
                  </button>
                </div>
              )}
            </div>
            {onClose && (
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
              >
                Close
              </button>
            )}
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-4 flex-wrap">
          {/* Zoom Controls */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-300">Zoom:</label>
            <div className="flex gap-1 bg-gray-700 rounded-lg p-1">
              {["year", "quarter", "month"].map((level) => (
                <button
                  key={level}
                  onClick={() => setZoomLevel(level)}
                  className={`px-3 py-1 text-sm rounded transition-colors ${
                    zoomLevel === level
                      ? "bg-blue-600 text-white"
                      : "text-gray-300 hover:bg-gray-600"
                  }`}
                >
                  {level.charAt(0).toUpperCase() + level.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Start Year Filter */}
          {yearOptions.length > 0 && (
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-300">Start Year:</label>
              <select
                value={startYear}
                onChange={(e) => setStartYear(parseInt(e.target.value))}
                className="px-3 py-1 bg-gray-700 text-white rounded-lg text-sm border border-gray-600"
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Clustering */}
          {data.clustering_enabled &&
            data.cluster_by_types &&
            data.cluster_by_types.length > 0 && (
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-300">Cluster By:</label>
                <select
                  value={clusterBy || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setClusterBy(value || null);
                  }}
                  className="px-3 py-1 bg-gray-700 text-white rounded-lg text-sm border border-gray-600"
                >
                  <option value="">None</option>
                  {data.cluster_by_types.map((type) => (
                    <option key={type} value={type}>
                      {type
                        .replace("-", " ")
                        .replace(/\b\w/g, (l) => l.toUpperCase())}
                    </option>
                  ))}
                </select>
              </div>
            )}

          {/* Filter Button */}
          {clusterBy && availableFilterOptions.length > 0 && (
            <div className="relative" ref={filterPanelRef}>
              <button
                onClick={() => setShowFilterPanel(!showFilterPanel)}
                className={`flex items-center gap-2 px-3 py-1 rounded-lg text-sm transition-colors ${
                  isFilterActive
                    ? "bg-blue-600 text-white"
                    : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                }`}
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                  />
                </svg>
                Filter{" "}
                {clusterBy
                  .replace("-", " ")
                  .replace(/\b\w/g, (l) => l.toUpperCase())}
                {isFilterActive && (
                  <span className="bg-white text-blue-600 px-1.5 py-0.5 rounded-full text-xs font-bold">
                    {currentClusterFilter.length}
                  </span>
                )}
              </button>

              {/* Filter Panel Dropdown */}
              {showFilterPanel && (
                <div className="absolute top-full left-0 mt-2 w-80 bg-gray-800 border border-gray-600 rounded-lg shadow-xl z-50">
                  <div className="p-3 border-b border-gray-600">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-white">
                        Filter by{" "}
                        {clusterBy
                          .replace("-", " ")
                          .replace(/\b\w/g, (l) => l.toUpperCase())}
                      </h3>
                      <button
                        onClick={() => setShowFilterPanel(false)}
                        className="text-gray-400 hover:text-white"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                    <input
                      type="text"
                      placeholder="Search..."
                      value={filterSearchTerm}
                      onChange={(e) => setFilterSearchTerm(e.target.value)}
                      className="w-full px-3 py-1.5 bg-gray-700 text-white rounded border border-gray-600 text-sm focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div className="p-2 border-b border-gray-600 flex gap-2">
                    <button
                      onClick={selectAllFilters}
                      className="flex-1 px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 rounded transition-colors"
                    >
                      Select All
                    </button>
                    <button
                      onClick={clearAllFilters}
                      className="flex-1 px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 rounded transition-colors"
                    >
                      Clear All (Show All)
                    </button>
                  </div>
                  <div className="max-h-80 overflow-y-auto p-2">
                    {/* Tree view for hierarchical cluster types */}
                    {isHierarchicalCluster && filterTreeData?.tree ? (
                      <>
                        {filterTreeData.tree.map((node, idx) => (
                          <TreeFilterNode
                            key={node.id || node.name || idx}
                            node={node}
                            depth={0}
                            selected={currentClusterFilter}
                            onToggle={toggleTreeNode}
                            searchTerm={filterSearchTerm}
                          />
                        ))}
                        {filterTreeData.hasUnassigned && (
                          <label
                            className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors ${
                              currentClusterFilter.includes("Unassigned")
                                ? "bg-blue-600/20 text-blue-300"
                                : "hover:bg-gray-700 text-gray-300"
                            }`}
                          >
                            <span className="w-4 flex-shrink-0" />
                            <input
                              type="checkbox"
                              checked={currentClusterFilter.includes(
                                "Unassigned"
                              )}
                              onChange={() => toggleFilterOption("Unassigned")}
                              className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-600 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-400 italic">
                              Unassigned
                            </span>
                          </label>
                        )}
                        {filterTreeData.tree.length === 0 &&
                          !filterTreeData.hasUnassigned && (
                            <div className="text-sm text-gray-500 text-center py-4">
                              No matching options
                            </div>
                          )}
                      </>
                    ) : (
                      /* Flat list for non-hierarchical cluster types */
                      <>
                        {availableFilterOptions
                          .filter((option) =>
                            option
                              .toLowerCase()
                              .includes(filterSearchTerm.toLowerCase())
                          )
                          .map((option) => (
                            <label
                              key={option}
                              className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors ${
                                currentClusterFilter.includes(option)
                                  ? "bg-blue-600/20 text-blue-300"
                                  : "hover:bg-gray-700 text-gray-300"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={currentClusterFilter.includes(option)}
                                onChange={() => toggleFilterOption(option)}
                                className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-600 rounded focus:ring-blue-500"
                              />
                              <span className="text-sm truncate">{option}</span>
                            </label>
                          ))}
                        {availableFilterOptions.filter((option) =>
                          option
                            .toLowerCase()
                            .includes(filterSearchTerm.toLowerCase())
                        ).length === 0 && (
                          <div className="text-sm text-gray-500 text-center py-4">
                            No matching options
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  <div className="p-3 border-t border-gray-600 flex items-center justify-between">
                    <span className="text-xs text-gray-400">
                      {isFilterActive
                        ? `Showing ${currentClusterFilter.length} of ${availableFilterOptions.length}`
                        : `Showing all ${availableFilterOptions.length}`}
                    </span>
                    <button
                      onClick={saveFilters}
                      disabled={!filtersDirty || savingFilters}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                        filtersDirty && !savingFilters
                          ? "bg-blue-600 hover:bg-blue-500 text-white"
                          : "bg-gray-700 text-gray-500 cursor-not-allowed"
                      }`}
                    >
                      {savingFilters ? (
                        <>
                          <svg
                            className="animate-spin w-4 h-4"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                          Saving...
                        </>
                      ) : (
                        <>
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                            />
                          </svg>
                          Save Filters
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Capability Level Filter (only for business-capability clustering) */}
          {clusterBy === "business-capability" && (
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-300">
                Capability Levels:
              </label>
              <div className="flex gap-1 flex-wrap">
                {[1, 2, 3, 4, 5].map((level) => (
                  <label
                    key={level}
                    className={`flex items-center gap-1 px-2 py-1 rounded text-xs cursor-pointer transition-colors ${
                      capabilityLevels.includes(level)
                        ? "bg-blue-600 text-white"
                        : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={capabilityLevels.includes(level)}
                      onChange={async (e) => {
                        const newLevels = e.target.checked
                          ? [...capabilityLevels, level].sort()
                          : capabilityLevels.filter((l) => l !== level);
                        setCapabilityLevels(newLevels);
                        // Re-execute report with new levels
                        if (reportId || report) {
                          setLoading(true);
                          try {
                            const response = await apiFetch(
                              `/api/reports/${reportId || report.id}/execute`,
                              {
                                method: "POST",
                                body: JSON.stringify({
                                  capability_levels: newLevels,
                                }),
                                headers: { "Content-Type": "application/json" },
                              }
                            );
                            if (response.ok) {
                              const result = await response.json();
                              if (result.success && result.result) {
                                setData(result.result);
                              }
                            } else {
                              const errorData = await response
                                .json()
                                .catch(() => ({}));
                              throw new Error(
                                errorData.message ||
                                  "Failed to update capability levels"
                              );
                            }
                          } catch (err) {
                            console.error("Error re-executing report:", err);
                            setError(
                              err.message ||
                                "Failed to update capability levels"
                            );
                          } finally {
                            setLoading(false);
                          }
                        }
                      }}
                      className="hidden"
                    />
                    <span>L{level}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Successors/Predecessors Toggle */}
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-300">
              <input
                type="checkbox"
                checked={showSuccessors}
                onChange={(e) => setShowSuccessors(e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-700 rounded"
              />
              Show Successors
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-300">
              <input
                type="checkbox"
                checked={showPredecessors}
                onChange={(e) => setShowPredecessors(e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-700 rounded"
              />
              Show Predecessors
            </label>
          </div>
        </div>
      </div>

      {/* Timeline View */}
      <div>
        {/* Handle tree structure for business capability clustering */}
        {displayData._isTree && treeRows ? (
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 m-4">
            <h3 className="text-lg font-semibold text-white mb-4">
              Business Capability Hierarchy
            </h3>

            {/* Legend - at top */}
            <div className="mb-4 pb-4 border-b border-gray-600">
              <div className="text-xs text-gray-400 mb-2">Legend:</div>
              <div className="flex flex-wrap gap-4">
                {Object.entries({
                  Planned: "#3b82f6",
                  "Phase In": "#eab308",
                  Active: "#22c55e",
                  "Phase Out": "#f97316",
                  "End of Life": "#ef4444",
                }).map(([status, color]) => (
                  <div key={status} className="flex items-center gap-2">
                    <div
                      className="w-4 h-4 rounded"
                      style={{ backgroundColor: color }}
                    ></div>
                    <span className="text-xs text-gray-300">{status}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Timeline Container */}
            <div className="border border-gray-600 rounded-lg overflow-x-auto">
              <div className="inline-block min-w-full">
                {/* Timeline Header */}
                <div className="flex border-b border-gray-600 bg-gray-800">
                  <div
                    className="flex-shrink-0 border-r border-gray-600 bg-gray-800 px-3 py-2 relative"
                    style={{
                      width: `${entityColumnWidth}px`,
                      position: "sticky",
                      left: 0,
                      zIndex: 40,
                      backgroundColor: "#1f2937",
                    }}
                  >
                    <div className="text-xs text-gray-400 font-medium">
                      Capability /{" "}
                      {entityType.charAt(0).toUpperCase() + entityType.slice(1)}
                    </div>
                    <div
                      className="absolute top-0 right-0 w-2 h-full cursor-col-resize hover:bg-blue-500 transition-colors bg-gray-600 hover:bg-blue-500"
                      onMouseDown={handleMouseDown}
                      style={{ zIndex: 50 }}
                      title="Drag to resize column"
                    />
                  </div>
                  <div
                    className="flex-1 relative"
                    style={{
                      minWidth: `${(filteredTimelineData || timelineData)?.intervals?.length * getIntervalWidth(zoomLevel) || 800}px`,
                    }}
                  >
                    <div className="flex">
                      {(filteredTimelineData || timelineData)?.intervals?.map(
                        (interval, idx) => (
                          <div
                            key={idx}
                            className="flex-1 text-center text-xs text-gray-400 border-l border-gray-600 px-1 py-2"
                            style={{
                              minWidth: `${getIntervalWidth(zoomLevel)}px`,
                            }}
                          >
                            <div className="font-medium">
                              {formatIntervalLabel(interval, zoomLevel)}
                            </div>
                            {zoomLevel === "month" && (
                              <div className="text-gray-500">
                                {interval.getFullYear()}
                              </div>
                            )}
                          </div>
                        )
                      )}
                    </div>
                  </div>
                </div>

                {/* Tree Rows */}
                <div className="divide-y divide-gray-600">
                  {treeRows.map((row, rowIdx) => {
                    if (row.type === "capability") {
                      const isExpanded = expandedEntities.has(row.id);
                      const hasExpandableContent = row.hasChildren;
                      return (
                        <div
                          key={`cap-${row.id}`}
                          className="flex items-center h-10 bg-gray-700/50 hover:bg-gray-700 transition-colors"
                        >
                          <div
                            className="flex-shrink-0 border-r border-gray-600 bg-gray-800 px-3 py-2 h-full flex items-center relative"
                            style={{
                              width: `${entityColumnWidth}px`,
                              position: "sticky",
                              left: 0,
                              zIndex: 20,
                              backgroundColor: "#1f2937",
                              paddingLeft: `${12 + row.depth * 20}px`,
                            }}
                          >
                            {hasExpandableContent ? (
                              <button
                                onClick={() => toggleEntityExpansion(row.id)}
                                className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-white transition-colors flex-shrink-0 mr-2"
                              >
                                {isExpanded ? "▼" : "▶"}
                              </button>
                            ) : (
                              <span className="w-4 flex-shrink-0 mr-2" />
                            )}
                            <span
                              className="text-sm font-semibold text-blue-300 flex-1"
                              style={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {row.name} ({row.entityCount})
                            </span>
                          </div>
                          <div
                            className="flex-1 relative h-full"
                            style={{
                              minWidth: `${(filteredTimelineData || timelineData)?.intervals?.length * getIntervalWidth(zoomLevel) || 800}px`,
                            }}
                          >
                            {/* Capability row - no timeline, just spacer */}
                          </div>
                        </div>
                      );
                    } else {
                      // Entity row
                      const entity = row.entity;
                      const entitySegments = (
                        filteredTimelineData || timelineData
                      )?.entitySegments?.find((s) => s.entity.id === entity.id);
                      const segments = entitySegments?.segments || [];
                      const statusColor = getLifecycleColor(
                        entity.lifecycle_status
                      );

                      return (
                        <div
                          key={`entity-${row.id}`}
                          className="flex items-center h-12 hover:bg-gray-750 transition-colors"
                        >
                          <div
                            className="flex-shrink-0 border-r border-gray-600 bg-gray-800 px-3 py-2 h-full flex items-center relative"
                            style={{
                              width: `${entityColumnWidth}px`,
                              position: "sticky",
                              left: 0,
                              zIndex: 20,
                              backgroundColor: "#1f2937",
                              paddingLeft: `${12 + row.depth * 20}px`,
                            }}
                          >
                            <div className="w-4 flex-shrink-0 mr-2"></div>
                            <span
                              className="text-sm font-medium text-white flex-1"
                              style={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {row.name}
                            </span>
                            <span
                              className={`px-2 py-0.5 rounded text-xs font-medium ${statusColor} text-white flex-shrink-0`}
                            >
                              {entity.lifecycle_status}
                            </span>
                          </div>
                          <div
                            className="flex-1 relative h-full"
                            style={{
                              minWidth: `${(filteredTimelineData || timelineData)?.intervals?.length * getIntervalWidth(zoomLevel) || 800}px`,
                            }}
                          >
                            {segments.length > 0 ? (
                              segments.map((segment, segIdx) => {
                                const style = getSegmentStyle(
                                  segment,
                                  (filteredTimelineData || timelineData)
                                    .startDate,
                                  (filteredTimelineData || timelineData).endDate
                                );
                                const color = getLifecycleColorHex(
                                  segment.status
                                );
                                return (
                                  <div
                                    key={segIdx}
                                    className="absolute h-full opacity-80 hover:opacity-100 transition-opacity cursor-pointer"
                                    style={{
                                      ...style,
                                      backgroundColor: color,
                                      top: "10%",
                                      height: "80%",
                                    }}
                                    title={`${segment.status}: ${formatDate(segment.start)} - ${segment.end ? formatDate(segment.end) : "Ongoing"}`}
                                  />
                                );
                              })
                            ) : (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <span className="text-xs text-gray-500">
                                  No timeline data
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    }
                  })}
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Standard flat grouping - unified grid with group headers
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 m-4">
            <h3 className="text-lg font-semibold text-white mb-4">
              {clusterBy
                ? `Grouped by ${clusterBy.replace("-", " ").replace(/\b\w/g, (l) => l.toUpperCase())}`
                : "All Entities"}
              <span className="text-gray-400 font-normal ml-2">
                ({Object.values(displayData).flat().length} entities in{" "}
                {Object.keys(displayData).length} groups)
              </span>
            </h3>

            {/* Legend - at top */}
            <div className="mb-4 pb-4 border-b border-gray-600">
              <div className="text-xs text-gray-400 mb-2">Legend:</div>
              <div className="flex flex-wrap gap-4">
                {Object.entries({
                  Planned: "#3b82f6",
                  "Phase In": "#eab308",
                  Active: "#22c55e",
                  "Phase Out": "#f97316",
                  "End of Life": "#ef4444",
                }).map(([status, color]) => (
                  <div key={status} className="flex items-center gap-2">
                    <div
                      className="w-4 h-4 rounded"
                      style={{ backgroundColor: color }}
                    ></div>
                    <span className="text-xs text-gray-300">{status}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Unified Timeline Container */}
            <div className="border border-gray-600 rounded-lg overflow-x-auto">
              <div className="inline-block min-w-full">
                {/* Timeline Header */}
                <div className="flex border-b border-gray-600 bg-gray-800">
                  <div
                    className="flex-shrink-0 border-r border-gray-600 bg-gray-800 px-3 py-2 relative"
                    style={{
                      width: `${entityColumnWidth}px`,
                      position: "sticky",
                      left: 0,
                      zIndex: 40,
                      backgroundColor: "#1f2937",
                    }}
                  >
                    <div className="text-xs text-gray-400 font-medium">
                      {clusterBy
                        ? clusterBy
                            .replace("-", " ")
                            .replace(/\b\w/g, (l) => l.toUpperCase())
                        : entityType.charAt(0).toUpperCase() +
                          entityType.slice(1)}{" "}
                      /{" "}
                      {entityType.charAt(0).toUpperCase() + entityType.slice(1)}
                    </div>
                    {/* Resize handle */}
                    <div
                      className="absolute top-0 right-0 w-2 h-full cursor-col-resize hover:bg-blue-500 transition-colors bg-gray-600 hover:bg-blue-500"
                      onMouseDown={handleMouseDown}
                      style={{ zIndex: 50 }}
                      title="Drag to resize column"
                    />
                  </div>
                  <div
                    className="flex-1 relative"
                    style={{
                      minWidth: `${(filteredTimelineData || timelineData).intervals.length * getIntervalWidth(zoomLevel)}px`,
                    }}
                  >
                    <div className="flex">
                      {(filteredTimelineData || timelineData).intervals.map(
                        (interval, idx) => (
                          <div
                            key={idx}
                            className="flex-1 text-center text-xs text-gray-400 border-l border-gray-600 px-1 py-2"
                            style={{
                              minWidth: `${getIntervalWidth(zoomLevel)}px`,
                            }}
                          >
                            <div className="font-medium">
                              {formatIntervalLabel(interval, zoomLevel)}
                            </div>
                            {zoomLevel === "month" && (
                              <div className="text-gray-500">
                                {interval.getFullYear()}
                              </div>
                            )}
                          </div>
                        )
                      )}
                    </div>
                  </div>
                </div>

                {/* Timeline Rows - Groups and Entities */}
                <div className="divide-y divide-gray-600">
                  {Object.entries(displayData).map(([groupName, entities]) => {
                    const uniqueEntities = entities.filter(
                      (entity, idx, self) =>
                        idx === self.findIndex((e) => e.id === entity.id)
                    );
                    const isGroupExpanded =
                      expandedEntities.has(`group-${groupName}`) ||
                      !clusterBy ||
                      Object.keys(displayData).length === 1;
                    const activeTimelineData =
                      filteredTimelineData || timelineData;

                    return (
                      <React.Fragment key={groupName}>
                        {/* Group Header Row */}
                        {clusterBy && Object.keys(displayData).length > 1 && (
                          <div className="flex items-center h-10 bg-gray-700/50 hover:bg-gray-700 transition-colors">
                            <div
                              className="flex-shrink-0 border-r border-gray-600 bg-gray-800 px-3 py-2 h-full flex items-center relative"
                              style={{
                                width: `${entityColumnWidth}px`,
                                position: "sticky",
                                left: 0,
                                zIndex: 20,
                                backgroundColor: "#1f2937",
                              }}
                            >
                              <button
                                onClick={() =>
                                  toggleEntityExpansion(`group-${groupName}`)
                                }
                                className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-white transition-colors flex-shrink-0 mr-2"
                              >
                                {isGroupExpanded ? "▼" : "▶"}
                              </button>
                              <span
                                className="text-sm font-semibold text-blue-300 flex-1"
                                style={{
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {groupName} ({uniqueEntities.length})
                              </span>
                            </div>
                            <div
                              className="flex-1 relative h-full"
                              style={{
                                minWidth: `${activeTimelineData.intervals.length * getIntervalWidth(zoomLevel)}px`,
                              }}
                            >
                              {/* Group header row - no timeline, just spacer */}
                            </div>
                          </div>
                        )}

                        {/* Entity Rows (shown when group is expanded) */}
                        {isGroupExpanded &&
                          uniqueEntities.map((entity) => {
                            const entitySegmentData =
                              activeTimelineData?.entitySegments?.find(
                                (s) => s.entity.id === entity.id
                              );
                            const segments = entitySegmentData?.segments || [];
                            const statusColor = getLifecycleColor(
                              entity.lifecycle_status
                            );
                            const hasSuccessors =
                              showSuccessors &&
                              entity.successors &&
                              entity.successors.length > 0;
                            const hasPredecessors =
                              showPredecessors &&
                              entity.predecessors &&
                              entity.predecessors.length > 0;
                            const isEntityExpanded = expandedEntities.has(
                              entity.id
                            );
                            const indentLevel =
                              clusterBy && Object.keys(displayData).length > 1
                                ? 1
                                : 0;

                            return (
                              <React.Fragment key={entity.id}>
                                <div className="flex items-center h-12 hover:bg-gray-750 transition-colors">
                                  <div
                                    className="flex-shrink-0 border-r border-gray-600 bg-gray-800 px-3 py-2 h-full flex items-center relative"
                                    style={{
                                      width: `${entityColumnWidth}px`,
                                      position: "sticky",
                                      left: 0,
                                      zIndex: 20,
                                      backgroundColor: "#1f2937",
                                      paddingLeft: `${12 + indentLevel * 20}px`,
                                    }}
                                  >
                                    <div className="flex items-center gap-2 w-full">
                                      {hasSuccessors || hasPredecessors ? (
                                        <button
                                          onClick={() =>
                                            toggleEntityExpansion(entity.id)
                                          }
                                          className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-white transition-colors flex-shrink-0"
                                          title={
                                            isEntityExpanded
                                              ? "Collapse relationships"
                                              : "Expand relationships"
                                          }
                                        >
                                          {isEntityExpanded ? (
                                            <svg
                                              className="w-3 h-3"
                                              fill="none"
                                              stroke="currentColor"
                                              viewBox="0 0 24 24"
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M19 9l-7 7-7-7"
                                              />
                                            </svg>
                                          ) : (
                                            <svg
                                              className="w-3 h-3"
                                              fill="none"
                                              stroke="currentColor"
                                              viewBox="0 0 24 24"
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M9 5l7 7-7 7"
                                              />
                                            </svg>
                                          )}
                                        </button>
                                      ) : (
                                        <div className="w-4 flex-shrink-0"></div>
                                      )}
                                      <span
                                        className="text-sm font-medium text-white flex-1"
                                        style={{
                                          whiteSpace: "nowrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                        }}
                                      >
                                        {entity.name}
                                      </span>
                                      <span
                                        className={`px-2 py-0.5 rounded text-xs font-medium ${statusColor} text-white flex-shrink-0`}
                                      >
                                        {entity.lifecycle_status}
                                      </span>
                                    </div>
                                  </div>

                                  <div
                                    className="flex-1 relative h-full"
                                    style={{
                                      minWidth: `${activeTimelineData.intervals.length * getIntervalWidth(zoomLevel)}px`,
                                    }}
                                  >
                                    {segments.length > 0 ? (
                                      segments.map((segment, segIdx) => {
                                        const style = getSegmentStyle(
                                          segment,
                                          activeTimelineData.startDate,
                                          activeTimelineData.endDate
                                        );
                                        const color = getLifecycleColorHex(
                                          segment.status
                                        );

                                        return (
                                          <div
                                            key={segIdx}
                                            className="absolute h-full opacity-80 hover:opacity-100 transition-opacity cursor-pointer"
                                            style={{
                                              ...style,
                                              backgroundColor: color,
                                              top: "10%",
                                              height: "80%",
                                            }}
                                            title={`${segment.status}: ${formatDate(segment.start)} - ${segment.end ? formatDate(segment.end) : "Ongoing"}`}
                                          />
                                        );
                                      })
                                    ) : (
                                      <div className="absolute inset-0 flex items-center justify-center">
                                        <span className="text-xs text-gray-500">
                                          No timeline data
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* Expanded relationships */}
                                {isEntityExpanded &&
                                  (hasSuccessors || hasPredecessors) && (
                                    <>
                                      {showSuccessors &&
                                        entity.successors?.map((succ) => {
                                          const succSegments =
                                            activeTimelineData?.entitySegments?.find(
                                              (s) => s.entity.id === succ.id
                                            );
                                          if (!succSegments) return null;

                                          return (
                                            <div
                                              key={`succ-${succ.id}`}
                                              className="flex items-center h-12 bg-gray-750/30"
                                            >
                                              <div
                                                className="flex-shrink-0 border-r border-gray-600 bg-gray-800 px-3 py-2 h-full flex items-center relative"
                                                style={{
                                                  width: `${entityColumnWidth}px`,
                                                  position: "sticky",
                                                  left: 0,
                                                  zIndex: 20,
                                                  backgroundColor: "#1f2937",
                                                  paddingLeft: `${12 + (indentLevel + 1) * 20}px`,
                                                }}
                                              >
                                                <div className="flex items-center gap-2 w-full">
                                                  <span
                                                    className="text-sm font-medium text-gray-400 italic flex-1"
                                                    style={{
                                                      whiteSpace: "nowrap",
                                                      overflow: "hidden",
                                                      textOverflow: "ellipsis",
                                                    }}
                                                  >
                                                    → {succ.name}
                                                  </span>
                                                </div>
                                              </div>
                                              <div
                                                className="flex-1 relative h-full"
                                                style={{
                                                  minWidth: `${activeTimelineData.intervals.length * getIntervalWidth(zoomLevel)}px`,
                                                }}
                                              >
                                                {succSegments.segments.length >
                                                0 ? (
                                                  succSegments.segments.map(
                                                    (segment, segIdx) => {
                                                      const style =
                                                        getSegmentStyle(
                                                          segment,
                                                          activeTimelineData.startDate,
                                                          activeTimelineData.endDate
                                                        );
                                                      const color =
                                                        getLifecycleColorHex(
                                                          segment.status
                                                        );

                                                      return (
                                                        <div
                                                          key={segIdx}
                                                          className="absolute h-full opacity-60 hover:opacity-100 transition-opacity cursor-pointer"
                                                          style={{
                                                            ...style,
                                                            backgroundColor:
                                                              color,
                                                            top: "15%",
                                                            height: "70%",
                                                            borderLeft:
                                                              "2px solid #60a5fa",
                                                          }}
                                                          title={`${segment.status}: ${formatDate(segment.start)} - ${segment.end ? formatDate(segment.end) : "Ongoing"} (Successor)`}
                                                        />
                                                      );
                                                    }
                                                  )
                                                ) : (
                                                  <div className="absolute inset-0 flex items-center justify-center">
                                                    <span className="text-xs text-gray-500">
                                                      No timeline data
                                                    </span>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      {showPredecessors &&
                                        entity.predecessors?.map((pred) => {
                                          const predSegments =
                                            activeTimelineData?.entitySegments?.find(
                                              (s) => s.entity.id === pred.id
                                            );
                                          if (!predSegments) return null;

                                          return (
                                            <div
                                              key={`pred-${pred.id}`}
                                              className="flex items-center h-12 bg-gray-750/30"
                                            >
                                              <div
                                                className="flex-shrink-0 border-r border-gray-600 bg-gray-800 px-3 py-2 h-full flex items-center relative"
                                                style={{
                                                  width: `${entityColumnWidth}px`,
                                                  position: "sticky",
                                                  left: 0,
                                                  zIndex: 20,
                                                  backgroundColor: "#1f2937",
                                                  paddingLeft: `${12 + (indentLevel + 1) * 20}px`,
                                                }}
                                              >
                                                <div className="flex items-center gap-2 w-full">
                                                  <span
                                                    className="text-sm font-medium text-gray-400 italic flex-1"
                                                    style={{
                                                      whiteSpace: "nowrap",
                                                      overflow: "hidden",
                                                      textOverflow: "ellipsis",
                                                    }}
                                                  >
                                                    ← {pred.name}
                                                  </span>
                                                </div>
                                              </div>
                                              <div
                                                className="flex-1 relative h-full"
                                                style={{
                                                  minWidth: `${activeTimelineData.intervals.length * getIntervalWidth(zoomLevel)}px`,
                                                }}
                                              >
                                                {predSegments.segments.length >
                                                0 ? (
                                                  predSegments.segments.map(
                                                    (segment, segIdx) => {
                                                      const style =
                                                        getSegmentStyle(
                                                          segment,
                                                          activeTimelineData.startDate,
                                                          activeTimelineData.endDate
                                                        );
                                                      const color =
                                                        getLifecycleColorHex(
                                                          segment.status
                                                        );

                                                      return (
                                                        <div
                                                          key={segIdx}
                                                          className="absolute h-full opacity-60 hover:opacity-100 transition-opacity cursor-pointer"
                                                          style={{
                                                            ...style,
                                                            backgroundColor:
                                                              color,
                                                            top: "15%",
                                                            height: "70%",
                                                            borderLeft:
                                                              "2px solid #f97316",
                                                          }}
                                                          title={`${segment.status}: ${formatDate(segment.start)} - ${segment.end ? formatDate(segment.end) : "Ongoing"} (Predecessor)`}
                                                        />
                                                      );
                                                    }
                                                  )
                                                ) : (
                                                  <div className="absolute inset-0 flex items-center justify-center">
                                                    <span className="text-xs text-gray-500">
                                                      No timeline data
                                                    </span>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })}
                                    </>
                                  )}
                              </React.Fragment>
                            );
                          })}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoadmapReportViewer;
